import React, { useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, DownloadIcon } from "lucide-react";
import styles from "../../DeviceHealth/FrequencyBucketTable.module.css";
import layerStyles from "./LayerLatencyTable.module.css";
import TCUStyles from "../../TCU.module.css";
import TablePagination from "../../DeviceHealth/TablePagination";
import { useLatencyContextData } from "../context/Context";
import useApiRows from "../useApiRows";
import { LATENCY_BUCKETS } from "./latencyBuckets";
import LayerMetricCells, { LayerLatencyHeader, displayValue } from "./LayerLatencyCells";
import LayerLatencyVinPopover, { LAYER_LATENCY_VINS_MODAL_ID } from "./LayerLatencyVinPopover";
import {
    LATENCY_DOWNLOAD_APIS,
    downloadCsv,
    getLayerLatencyCityWise,
    getLayerLatencyDepotWise
} from "../LatencyService";

const ROWS_PER_PAGE = 10; // fixed, not user-changeable
const FIRST_COLUMNS = ["City / Depot", "Active VIN"];
const COL_COUNT = FIRST_COLUMNS.length + 2 + LATENCY_BUCKETS.length;

// bootstrap 4 + 5 attributes so the shared CustomModal opens either way
const MODAL_TRIGGER = {
    "data-toggle": "modal",
    "data-bs-toggle": "modal",
    "data-target": `#${LAYER_LATENCY_VINS_MODAL_ID}`,
    "data-bs-target": `#${LAYER_LATENCY_VINS_MODAL_ID}`
};

const toOption = (value) => [{ label: value, value }];

function ChartLoader() {
    return (
        <div className={styles.loadingState}>
            <span className={styles.spinner} />
            <span>Loading...</span>
        </div>
    );
}

/*
 * City -> depot accordion (same pattern as the Device Bucketing table);
 * clicking a depot opens its VINs in a popover.
 */
export default function LayerLatencyTable({
    title = "Layer-wise latency breakdown",
    subtitle = "Click a city to view its depots, a depot to view its VINs"
}) {
    const { GlobalFilter } = useLatencyContextData();
    const [openCity, setOpenCity] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [popoverSelection, setPopoverSelection] = useState(null);
    const [isOn, setIsOn] = useState(true);

    // API 1: city-wise rows
    const [cityRows, isLoading] = useApiRows(getLayerLatencyCityWise, { ...GlobalFilter, ignitionOn: isOn, });

    // API 2: depots of the opened city
    const [depotRows, isDepotLoading] = useApiRows(
        getLayerLatencyDepotWise,
        { ...GlobalFilter, ignitionOn: isOn, city: toOption(openCity) },
        !openCity
    );

    // new filters: back to page 1 with every city closed
    useEffect(() => {
        setCurrentPage(1);
        setOpenCity(null);
    }, [GlobalFilter]);

    const totalItems = cityRows.length;
    const totalPages = Math.max(1, Math.ceil(totalItems / ROWS_PER_PAGE));
    const safePage = Math.min(currentPage, totalPages);

    const paginatedRows = useMemo(() => {
        const start = (safePage - 1) * ROWS_PER_PAGE;
        return cityRows.slice(start, start + ROWS_PER_PAGE);
    }, [cityRows, safePage]);

    const toggleCity = (city) => setOpenCity((prev) => (prev === city ? null : city));

    const changePage = (page) => {
        setOpenCity(null);
        setCurrentPage(page);
    };

    const stateRow = (rowClass, content) => (
        <tr className={rowClass}>
            <td colSpan={COL_COUNT}>{content}</td>
        </tr>
    );

    /* ---------- depot rows: click opens the VIN popover ---------- */
    const renderDepotRows = () => {
        if (isDepotLoading) return stateRow(styles.depotRow, <ChartLoader />);
        if (!depotRows.length) {
            return stateRow(styles.depotRow, <div className={styles.loadingState}>No depots found</div>);
        }

        return depotRows.map((row, idx) => (
            <tr
                key={`depot-${row.Depot || idx}`}
                {...MODAL_TRIGGER}
                className={`${styles.depotRow} ${styles.depotRowClickable}`}
                title={`${row.Depot} — click to view VINs`}
                onClick={() => setPopoverSelection({ city: row.City ?? openCity, depot: row.Depot })}
            >
                <td className={styles.depotCell}>
                    <span className={styles.cityContent}>
                        <span className={styles.depotIndicator} />
                        <button type="button" className={styles.depotLink}>
                            {displayValue(row.Depot)}
                        </button>
                    </span>
                </td>
                <td>{displayValue(row.activeVin)}</td>
                <LayerMetricCells row={row} />
            </tr>
        ));
    };

    /* ---------- city rows ---------- */
    const renderCityRow = (row) => {
        const isOpen = openCity === row.City;

        return (
            <React.Fragment key={row.City}>
                <tr
                    className={`${styles.cityRow} ${isOpen ? styles.cityRowActive : ""}`}
                    onClick={() => toggleCity(row.City)}
                >
                    <td className={styles.cityCell}>
                        <span className={styles.cityContent}>
                            <span className={styles.accordionIcon}>
                                {isOpen ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
                            </span>
                            <span
                                className={styles.cityName}
                                title={`${row.City} — click to ${isOpen ? "hide" : "view"} depots`}
                            >
                                {row.City}
                            </span>
                        </span>
                    </td>
                    <td className={styles.bucketCell}>{displayValue(row.activeVin)}</td>
                    <LayerMetricCells row={row} />
                </tr>

                {isOpen && renderDepotRows()}
            </React.Fragment>
        );
    };

    return (
        <div className={`card ${styles.card} mx-3 mt-2 mb-2`}>
            <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
                <div className={TCUStyles.titleWrap}>
                    <span className={TCUStyles.titleBar} />
                    <span className={TCUStyles.title}>{title}</span>
                    <span className={TCUStyles.subtitle}>{subtitle}</span>
                </div>
                <div>
                    <label className={`${styles.ignition}`} >
                        <span className={styles.label}>Ignition</span>
                        <span className={styles.switch}>
                            <input
                                type="checkbox"
                                role="switch"
                                className={styles.input}
                                checked={isOn}
                                onChange={(e) => {
                                    setIsOn(e.target.checked);
                                    // onChange?.(e.target.checked);
                                }}
                            />
                            <span className={styles.track} />
                        </span>
                        <span className={styles.state}>{isOn ? "ON" : "OFF"}</span>
                    </label>


                    <button
                        type="button"
                        className={`${TCUStyles.downloadBtn} custom-Btn-animation`}
                        title="Download layer-wise latency (CSV)"
                        disabled={isLoading || !totalItems}
                        onClick={() =>
                            downloadCsv(LATENCY_DOWNLOAD_APIS.layerLatency, { ...GlobalFilter, ignitionOn: isOn }, title).catch(() => { })
                        }
                    >
                        <DownloadIcon size={15} />
                    </button>
                </div>
            </div>

            <div className={`table-responsive mx-2 mt-1 ${styles.tableWrapper}`}>
                <table className={`table table-bordered table-hover align-middle mb-0 ${styles.mainTable} ${layerStyles.table}`}>
                    <colgroup>
                        <col className={layerStyles.colLocation} />
                        <col className={layerStyles.colActive} />
                        <col className={layerStyles.colPct} />
                        {LATENCY_BUCKETS.map((b) => (
                            <col key={b.field} className={layerStyles.colBucket} />
                        ))}
                        <col className={layerStyles.colOverall} />
                    </colgroup>

                    <LayerLatencyHeader firstColumns={FIRST_COLUMNS} layerStyles={layerStyles} />

                    <tbody>
                        {isLoading ? (
                            stateRow("", <ChartLoader />)
                        ) : totalItems === 0 ? (
                            stateRow("", <div className={styles.loadingState}>No cities found</div>)
                        ) : (
                            paginatedRows.map(renderCityRow)
                        )}
                    </tbody>

                    <tfoot className={layerStyles.notesFoot}>
                        <tr>
                            <td colSpan={COL_COUNT}>
                                <span><strong>Actual %</strong> — Percentage of active vehicles out of all vehicles</span>
                                <span><strong>Overall %</strong> — Percentage of messages received within 30 seconds</span>
                            </td>
                        </tr>
                    </tfoot>
                </table>
            </div>

            {!isLoading && (
                <TablePagination
                    page={safePage}
                    totalPages={totalPages}
                    totalItems={totalItems}
                    pageSize={ROWS_PER_PAGE}
                    onPageChange={changePage}
                    itemLabel="cities"
                />
            )}

            <LayerLatencyVinPopover selection={popoverSelection} isOn={isOn} />
        </div>
    );
}
